/**
 * CountryDialPicker — phone input with a country dial-code picker.
 *
 * Single-field controlled component. The stored value is the combined
 * `"+<dial>-<digits>"` form Flutter ships on the wire (e.g.
 * `"+971-54354445"`). Internal state parses the value into dial + iso2 +
 * local digits for the UI, and recomposes on every change.
 *
 * Validation (e.g. Twilio Lookups) stays out of this component — pass
 * the parsed digits to the validator yourself if needed.
 *
 * History: extracted in PROD-777 from inline copies in
 *   - `src/scheduling/components/appointments/sections/create-patient-view.tsx`
 *   - `src/scheduling/components/appointments/sections/create-contact-view.tsx`
 *   - `src/patients/components/personal-details/demographics-section.tsx`
 *
 * Country list mirrors the Flutter PA's dial-code picker: GCC + IN +
 * UM/CA/US + tail. Flag PNGs come from flagcdn.com because Windows
 * doesn't render flag emojis.
 */

import { useEffect, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CountryEntry {
  iso2: string;
  dial: string;
  name: string;
}

export const COUNTRIES: readonly CountryEntry[] = [
  { iso2: 'ae', dial: '+971', name: 'United Arab Emirates' },
  { iso2: 'eg', dial: '+20', name: 'Egypt' },
  { iso2: 'qa', dial: '+974', name: 'Qatar' },
  { iso2: 'sa', dial: '+966', name: 'Saudi Arabia' },
  { iso2: 'om', dial: '+968', name: 'Oman' },
  { iso2: 'kw', dial: '+965', name: 'Kuwait' },
  { iso2: 'bh', dial: '+973', name: 'Bahrain' },
  { iso2: 'in', dial: '+91', name: 'India' },
  { iso2: 'ca', dial: '+1', name: 'Canada' },
  { iso2: 'us', dial: '+1', name: 'United States' },
  { iso2: 'gb', dial: '+44', name: 'United Kingdom' },
  { iso2: 'pk', dial: '+92', name: 'Pakistan' },
  { iso2: 'ph', dial: '+63', name: 'Philippines' },
  { iso2: 'ng', dial: '+234', name: 'Nigeria' },
  { iso2: 'jo', dial: '+962', name: 'Jordan' },
  { iso2: 'lb', dial: '+961', name: 'Lebanon' },
] as const;

/** Real PNG flag, since Windows doesn't render flag emojis. */
export function flagSrc(iso2: string): string {
  return `https://flagcdn.com/24x18/${iso2.toLowerCase()}.png`;
}

/** Parse a stored phone string into picker parts.
 *
 * Tolerant of the messy shapes that already exist on the wire (the same
 * `officePhone`/`faxNumber`/`phoneNumber` fields are also written by the
 * Flutter app and by legacy data):
 *   - leading/trailing whitespace, e.g. `" +971-585559695"`
 *   - a missing dash separator, e.g. `"+97123456789"` (Flutter ships
 *     `"{phoneCode}-{digits}"` but older rows merged them)
 *   - a bare dial code with no `+`, e.g. `"971-554645645"`
 * The clean canonical `"+dial-digits"` form still takes the fast path and
 * parses identically — so existing consumers are unaffected. Anything that
 * can't be matched keeps ALL its digits as the local number (never silently
 * dropped), so opening + re-saving a malformed value normalises it rather
 * than wiping it. */
export function splitPhone(stored: string): {
  dial: string;
  local: string;
  iso2: string;
} {
  const s = (stored ?? '').trim();
  if (!s) return { dial: '+971', local: '', iso2: 'ae' };

  // Fast path: dash-separated, known dial code (with or without leading +).
  const dash = s.match(/^\+?(\d+)\s*-\s*(.*)$/);
  if (dash) {
    const dial = `+${dash[1]}`;
    const known = COUNTRIES.find((c) => c.dial === dial);
    if (known) {
      return { dial, local: dash[2].replace(/\D+/g, ''), iso2: known.iso2 };
    }
  }

  // Fallback: peel the longest known dial code off the raw digit string
  // (handles the no-dash / unknown-format rows).
  const digits = s.replace(/\D+/g, '');
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    const code = c.dial.slice(1); // strip the leading '+'
    if (digits.startsWith(code)) {
      return { dial: c.dial, local: digits.slice(code.length), iso2: c.iso2 };
    }
  }

  // Give up on the country: default UAE, but keep every digit as the number.
  return { dial: '+971', local: digits, iso2: 'ae' };
}

/** Compose picker parts into the stored `+dial-digits` form. Empty
 *  digits collapse to empty string so save-gating can detect an unset
 *  phone without checking for a stray dial-only prefix. */
export function joinPhone(dial: string, local: string): string {
  const digits = local.replace(/\D+/g, '');
  return digits ? `${dial}-${digits}` : '';
}

export interface CountryDialPickerProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  /** Error state — applies a red ring instead of the brand ring on focus. */
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
  /** Optional id for the input (for label `htmlFor`). */
  id?: string;
  /** Control height to align with sibling inputs:
   *  `sm` = h-8 (32px, shadcn `<Input>` — the default, matches most forms),
   *  `md` = h-10 (36px, Signal `TextField size="compact"` — billing),
   *  `lg` = h-10 (40px, forms whose inputs force h-10 — onboarding,
   *  create-practitioner). Defaults to `sm`. */
  size?: 'sm' | 'md' | 'lg';
}

export function CountryDialPicker({
  value,
  onChange,
  placeholder = 'e.g. 23 345 4567',
  invalid = false,
  disabled = false,
  className,
  id,
  size = 'sm',
}: CountryDialPickerProps) {
  const heightClass = size === 'lg' ? 'h-10' : size === 'md' ? 'h-10' : 'h-8';
  const initial = splitPhone(value);
  const [dial, setDial] = useState(initial.dial);
  const [iso2, setIso2] = useState(initial.iso2);
  const [local, setLocal] = useState(initial.local);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Re-sync internal state when the form value changes externally (e.g.
  // RHF reset on edit-mode load).
  useEffect(() => {
    const next = splitPhone(value);
    setDial(next.dial);
    setIso2(next.iso2);
    setLocal(next.local);
  }, [value]);

  // Push composed value to the parent on change.
  useEffect(() => {
    const next = joinPhone(dial, local);
    if (next !== value) onChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dial, local]);

  return (
    <div className={cn('flex items-stretch gap-2 relative', className)}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setPickerOpen((o) => !o);
          setSearch('');
        }}
        className={cn(
          'flex items-center gap-2 px-2 rounded-md bg-grey-100 hover:bg-grey-200 disabled:opacity-50 disabled:cursor-not-allowed',
          heightClass,
        )}
      >
        <img
          src={flagSrc(iso2)}
          alt={iso2.toUpperCase()}
          width={24}
          height={18}
          className="rounded-sm flex-shrink-0"
          loading="lazy"
        />
        <span className="text-sm text-text-primary tabular-nums">{dial}</span>
        <ChevronDown className="h-3 w-3 text-text-muted" />
      </button>
      <input
        id={id}
        type="tel"
        inputMode="tel"
        disabled={disabled}
        value={local}
        onChange={(e) =>
          setLocal(e.target.value.replace(/[^\d\s]/g, '').slice(0, 20))
        }
        placeholder={placeholder}
        className={cn(
          'flex-1 px-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1',
          heightClass,
          invalid ? 'focus:ring-status-error' : 'focus:ring-brand-primary',
          disabled ? 'opacity-50 cursor-not-allowed' : '',
        )}
      />
      {pickerOpen ? (
        <CountryPicker
          selectedDial={dial}
          search={search}
          onSearchChange={setSearch}
          onSelect={(c) => {
            setDial(c.dial);
            setIso2(c.iso2);
            setPickerOpen(false);
            setSearch('');
          }}
          onClose={() => setPickerOpen(false)}
        />
      ) : null}
    </div>
  );
}

interface CountryPickerProps {
  selectedDial: string;
  search: string;
  onSearchChange: (next: string) => void;
  onSelect: (country: CountryEntry) => void;
  onClose: () => void;
}

function CountryPicker({
  selectedDial,
  search,
  onSearchChange,
  onSelect,
  onClose,
}: CountryPickerProps) {
  const q = search.trim().toLowerCase();
  const visible = q
    ? COUNTRIES.filter(
        (c) =>
          c.iso2.includes(q) ||
          c.dial.toLowerCase().includes(q) ||
          c.name.toLowerCase().includes(q),
      )
    : COUNTRIES;
  return (
    <>
      <button
        type="button"
        aria-label="Close country picker"
        onClick={onClose}
        className="fixed inset-0 z-10 cursor-default"
      />
      <div className="absolute z-20 left-0 top-full mt-1 w-[264px] rounded-md border border-border-default bg-surface-card shadow-lg overflow-hidden">
        <div className="p-2 border-b border-border-subtle">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-text-hint pointer-events-none" />
            <input
              autoFocus
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search country"
              className="w-full h-10 pl-8 pr-3 rounded-md bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:bg-surface-card focus:outline-none focus:ring-1 focus:ring-brand-primary"
            />
          </div>
        </div>
        <div className="max-h-[280px] overflow-y-auto scrollbar-hide py-1">
          {visible.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-4">
              No matching countries.
            </p>
          ) : (
            visible.map((c) => (
              <button
                key={`${c.iso2}-${c.dial}`}
                type="button"
                onClick={() => onSelect(c)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-grey-100',
                  c.dial === selectedDial ? 'bg-blue-100' : '',
                )}
              >
                <img
                  src={flagSrc(c.iso2)}
                  alt={c.iso2.toUpperCase()}
                  width={24}
                  height={18}
                  className="rounded-sm flex-shrink-0"
                  loading="lazy"
                />
                <span className="text-sm text-text-primary tabular-nums">
                  {c.dial}
                </span>
                <span className="text-xs text-text-muted ml-auto">
                  ({c.iso2.toUpperCase()})
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </>
  );
}
